import type { MetaFunction } from "@remix-run/node";

export const meta: MetaFunction = () => [
  { title: "FAQ — Sandcastle Admin Search" },
  {
    name: "description",
    content:
      "Frequently asked questions about Sandcastle Admin Search, the Shopify product management app.",
  },
];

const APP_NAME = "Sandcastle Admin Search";
const CONTACT_EMAIL = "support@sandcastle-web.com";

interface QA {
  q: string;
  a: React.ReactNode;
}

const faqs: QA[] = [
  {
    q: "What does this app do?",
    a: (
      <>
        It&rsquo;s a power-user replacement for Shopify admin&rsquo;s built-in
        product list. {APP_NAME} adds multi-field search, audit filters
        (missing images, no SKU, metafield value, etc.), sortable columns
        Shopify doesn&rsquo;t support natively, saved views, a duplicate
        finder, CSV export of filtered results, and bulk-edit shortcuts.
      </>
    ),
  },
  {
    q: "Is it free?",
    a: (
      <>
        Yes, currently. We may introduce paid tiers in the future, with
        reasonable advance notice and no surprise charges. See the{" "}
        <a href="/terms">terms of service</a> for details.
      </>
    ),
  },
  {
    q: "Will it slow down my store?",
    a: (
      <>
        No. {APP_NAME} runs entirely inside the Shopify admin and your
        merchant browser; it does not load anything on your storefront and
        does not affect page speed for shoppers.
      </>
    ),
  },
  {
    q: "Will it work with a large catalogue?",
    a: (
      <>
        Yes. Search, most filters and the sorts Shopify supports load one
        page at a time, so they stay fast however many products you have.
        Some things need every matching product checked: sorting by price,
        SKU or barcode, and filtering by image presence, variant count,
        sale price or metafield value. Those take a couple of seconds the first time on a big
        store (about two seconds for 5,000 products), and are instant for
        15 minutes afterwards. Above 5,000 products, the first 5,000 appear
        straight away and the page updates itself with your complete
        catalogue a few seconds later. The duplicate finder checks up to
        2,000 products by default.
      </>
    ),
  },
  {
    q: "What permissions does the app request?",
    a: (
      <>
        Two OAuth scopes:
        <ul>
          <li>
            <code>write_products</code> &mdash; needed to read product data
            and to apply bulk edits (status changes, vendor, tags, etc.)
          </li>
          <li>
            <code>read_publications</code> &mdash; needed to populate the
            sales-channel filter
          </li>
        </ul>
        We do not request access to customer data, orders, payment
        information, or any other store data.
      </>
    ),
  },
  {
    q: "Is my data safe?",
    a: (
      <>
        We do not store product, customer, or order data on our servers.
        We only persist your per-shop preferences (search field defaults,
        column visibility, saved views, page size). Product data is read
        live from Shopify&rsquo;s API on each request. See our{" "}
        <a href="/privacy">privacy policy</a> for full details, including
        the third-party processors we use (Fly.io for hosting, Neon for
        database, Sentry for error monitoring).
      </>
    ),
  },
  {
    q: "How do I uninstall?",
    a: (
      <>
        From your Shopify admin: Apps &rarr; Sandcastle Admin Search &rarr;
        remove. Shopify will notify us, and we delete your session and
        per-shop preferences within 48 hours per GDPR requirements.
      </>
    ),
  },
  {
    q: "What&rsquo;s the difference between &ldquo;Quick edit&rdquo; and &ldquo;Open in Shopify editor&rdquo;?",
    a: (
      <>
        Both appear when you select one or more products.
        <ul>
          <li>
            <strong>Quick edit</strong> is an inline modal inside our app
            for fast changes: status, vendor, type, add tags, remove
            tags. Edits apply immediately via the Shopify API.
          </li>
          <li>
            <strong>Open in Shopify editor</strong> hands the selected
            products off to Shopify&rsquo;s native bulk editor &mdash; a
            spreadsheet-style UI that supports many more columns (variant
            pricing, inventory, taxonomy, images, etc.). When you click
            back, you return to our app at the same view you left.
          </li>
        </ul>
      </>
    ),
  },
  {
    q: "Why do some bulk actions open in a new tab?",
    a: (
      <>
        Some Shopify admin flows &mdash; Delete, Unlist, Add to collection,
        Add to sales channel, etc. &mdash; are popups overlaid on
        Shopify&rsquo;s native products page. Opening them in a new tab
        lets you complete the action without losing your place in our app.
        Close the new tab when you&rsquo;re done and your selection and
        filters are still where you left them.
      </>
    ),
  },
  {
    q: "Why is your search better than Shopify&rsquo;s built-in?",
    a: (
      <>
        Shopify&rsquo;s admin product list has a single search box that
        matches across all fields with no filter scoping. {APP_NAME} lets
        you target specific fields (e.g. &ldquo;search only in barcodes&rdquo;)
        and combine search with structured filters Shopify&rsquo;s native
        UI doesn&rsquo;t expose &mdash; metafield value matching, image
        presence, no-SKU detection, multi-variant filtering, and more.
      </>
    ),
  },
  {
    q: "Do saved views transfer between stores if I install on multiple shops?",
    a: (
      <>
        No. Saved views are stored per shop. If you install {APP_NAME} on a
        second store, that installation gets its own set of saved views.
      </>
    ),
  },
  {
    q: "Does the CSV export include all my products?",
    a: (
      <>
        It exports the products matching your current filters and sort
        order, with the columns you have visible in the table. There&rsquo;s
        a soft cap of 5,000 products per export to keep response times
        reasonable. If you need more, narrow the filter first.
      </>
    ),
  },
  {
    q: "I have a feature request or bug to report.",
    a: (
      <>
        Email{" "}
        <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a>. Please
        include your shop domain, what you were trying to do, and what
        actually happened. Screenshots help a lot.
      </>
    ),
  },
];

export default function FAQ() {
  return (
    <main style={pageStyle}>
      <article style={articleStyle}>
        <header>
          <h1 style={h1Style}>Frequently asked questions</h1>
          <p style={metaStyle}>
            Everything we&rsquo;re commonly asked about {APP_NAME}.
          </p>
        </header>

        {faqs.map(({ q, a }) => (
          <section key={q}>
            <h2 style={h2Style}>{q}</h2>
            <div style={answerStyle}>{a}</div>
          </section>
        ))}

        <footer style={footerStyle}>
          <p>
            New features and fixes are listed on{" "}
            <a href="/changelog">What&rsquo;s new</a>. Still stuck? Email{" "}
            <a href={`mailto:${CONTACT_EMAIL}`}>{CONTACT_EMAIL}</a> &mdash;
            we read every message.
          </p>
        </footer>
      </article>
    </main>
  );
}

const pageStyle: React.CSSProperties = {
  minHeight: "100vh",
  backgroundColor: "#f6f6f7",
  padding: "48px 24px",
  fontFamily:
    "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
  color: "#202223",
  lineHeight: 1.6,
};

const articleStyle: React.CSSProperties = {
  maxWidth: 760,
  margin: "0 auto",
  backgroundColor: "#ffffff",
  borderRadius: 12,
  padding: "48px 56px",
  boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
};

const h1Style: React.CSSProperties = {
  fontSize: 32,
  fontWeight: 600,
  margin: "0 0 8px",
};

const h2Style: React.CSSProperties = {
  fontSize: 18,
  fontWeight: 600,
  margin: "32px 0 8px",
};

const metaStyle: React.CSSProperties = {
  color: "#6d7175",
  fontSize: 14,
  margin: "0 0 24px",
};

const answerStyle: React.CSSProperties = {
  marginBottom: 8,
};

const footerStyle: React.CSSProperties = {
  marginTop: 48,
  paddingTop: 24,
  borderTop: "1px solid #e1e3e5",
  color: "#6d7175",
  fontSize: 14,
};
